/**
 * One pending proposal.
 *
 * Everything a person needs to answer it without opening anything else: what it
 * is, who raised it, what happens if nobody acts, who has already tapped and
 * who has not, and three ways to answer.
 *
 * "Who has not voted" is deliberately as prominent as "who has" — under
 * unanimity (docs/01 §4.1) a single missing tap is the only thing between a
 * proposal and its deadline, and naming the person is how it gets chased.
 *
 * Two visual rules, both from the domain rather than from taste:
 *
 *  - The card that is **waiting on you** carries the focus ring, because it is
 *    the only one you can do anything about.
 *  - A **money** proposal is indigo and never carries a countdown. It is a
 *    different kind of decision, not a more urgent one.
 */

import type { MemberId } from '@office/domain'
import { AutoApprove } from './Countdown.js'
import { describeCounter, type ProposalView } from './proposal-data.js'
import { nameOf, type MemberLite } from '../chores/live.js'
import '../chores/phone.css'
import './proposals.css'

export interface ProposalCardProps {
  view: ProposalView
  members: readonly MemberLite[]
  me: MemberId
  now: number
  onApprove(view: ProposalView): void
  onCounter(view: ProposalView): void
  onExplain(view: ProposalView): void
  onCancel(view: ProposalView): void
}

export function ProposalCard({
  view,
  members,
  me,
  now,
  onApprove,
  onCounter,
  onExplain,
  onCancel,
}: ProposalCardProps) {
  const { proposal, summary } = view

  return (
    <li
      className="card pr-card"
      data-testid="proposal"
      data-money={view.money ? 'true' : 'false'}
      data-awaiting={view.awaitingMe ? 'true' : 'false'}
    >
      <div className="pr-tags">
        <span className="ph-chip">{view.label}</span>
        {view.money ? (
          <span className="ph-chip" data-testid="money-badge" data-tone="accent">
            Money
          </span>
        ) : null}
        {view.awaitingMe ? (
          <span className="ph-chip" data-testid="awaiting-me">
            Waiting on you
          </span>
        ) : null}
      </div>

      <div>
        <h3 className="pr-title">{proposal.title}</h3>
        <p className="pr-by">Raised by {view.proposerName}</p>
      </div>

      {proposal.details !== null && proposal.details !== '' ? (
        <p className="pr-details">{proposal.details}</p>
      ) : null}

      <AutoApprove
        proposal={proposal}
        type={view.type}
        now={now}
        awaitingCount={summary.awaiting.length}
      />

      <VoterChips view={view} members={members} me={me} />

      {view.counters.length > 0 ? (
        <ul className="pr-counters" aria-label="Suggested alternatives">
          {view.counters.map(c => (
            <li key={`${String(c.voterId)}-${c.votedAt}`} className="pr-counter">
              {nameOf(members, c.voterId)} suggested: {describeCounter(c)}
            </li>
          ))}
        </ul>
      ) : null}

      {view.awaitingMe ? (
        <div className="pr-actions">
          <button type="button" className="btn btn-primary" onClick={() => onApprove(view)}>
            Approve
          </button>
          <button type="button" className="btn" onClick={() => onCounter(view)}>
            No — try another time
          </button>
          <button type="button" className="btn" onClick={() => onExplain(view)}>
            No — explain
          </button>
        </div>
      ) : (
        <p className="pr-standing">
          {view.mine
            ? 'You raised this, which counts as a yes.'
            : view.votedByMe
              ? 'You have already voted.'
              : // Not the viewer's vote to cast — interns do not vote (docs/01 §1).
                'Only the owners vote on this.'}
        </p>
      )}

      {view.canCancel ? (
        <button
          type="button"
          className="btn pr-withdraw"
          onClick={() => onCancel(view)}
        >
          Withdraw
        </button>
      ) : null}
    </li>
  )
}

export interface VoterChipsProps {
  view: ProposalView
  members: readonly MemberLite[]
  me: MemberId
}

/**
 * Who has voted and who has not — the whole tally, always.
 *
 * A recorded "no" is amber: a veto is a decision, not an error, and `--danger`
 * stays reserved for a failed sync or a rejected write (docs/03).
 */
export function VoterChips({ view, members, me }: VoterChipsProps) {
  const { summary, proposal } = view
  const label = (id: MemberId): string => (id === me ? 'You' : nameOf(members, id))

  return (
    <ul className="pr-votes" aria-label="Votes so far">
      {summary.approvedBy.map(id => (
        <li key={`a-${String(id)}`} className="ph-chip" data-vote="approve" data-tone="good">
          <span aria-hidden="true">✓ </span>
          {id === proposal.createdBy
            ? `${label(id)} raised it — counts as yes`
            : `${label(id)} approved`}
        </li>
      ))}
      {summary.rejectedBy.map(id => (
        <li key={`r-${String(id)}`} className="ph-chip" data-vote="reject" data-tone="warn">
          <span aria-hidden="true">✗ </span>
          {label(id)} said no
        </li>
      ))}
      {summary.awaiting.map(id => (
        <li key={`w-${String(id)}`} className="ph-chip" data-vote="awaiting" data-tone="neutral">
          <span aria-hidden="true">… </span>
          Waiting on {label(id)}
        </li>
      ))}
    </ul>
  )
}
